/**
 * Typed wrapper around window.umami (self-hosted "Jorney" analytics).
 *
 * Docs:
 * - https://docs.umami.is/docs/tracker-functions
 * - https://docs.umami.is/docs/track-events
 * - https://docs.umami.is/docs/distinct-ids
 *
 * Umami limits enforced here (violations cause HTTP 400 or silent drops):
 * - event name <= 50 chars and must not start with `=`, `+`, `-`, `@`
 * - event data is a FLAT object of string / number / boolean
 * - strings <= 500 chars, <= 50 properties
 *
 * Never send passwords, tokens, form answers or free text typed by students.
 */

export type UmamiEventPayload = Record<string, string | number | boolean | null | undefined>

/**
 * Typed map of the product events sent to Umami.
 *
 * Adding an event: declare the name + payload shape here, then call
 * `trackEvent('my_event', { ... })`. Use snake_case, no spaces.
 */
export type UmamiEvents = {
  // User lifecycle
  login_completed: { method?: 'password' | 'oauth' }
  signup_completed: { user_id?: string; method?: 'password' | 'oauth' }
  sso_completed: Record<string, never>
  session_expired: Record<string, never>

  // Products and lessons
  product_opened: { product_id: string; area: string; duration_ms: number }
  lesson_started: { lesson_id?: string; product_id?: string }
  lesson_completed: { lesson_id?: string; product_id?: string; auto: boolean }
  tool_submitted: { tool_type: string }

  // Commerce
  locked_content_viewed: { product_id?: string; category_id?: string; kind: string }
  purchase_cta_clicked: { product_id?: string; category_id?: string; native?: boolean }

  // Certificates and community
  certificate_issued: { product_id?: string }
  certificate_downloaded: { product_id?: string }
  completion_cta_clicked: { product_id?: string }
  community_entered: Record<string, never>

  // Embeds / errors
  embed_opened: { widget_type?: string }
  app_error: { name: string; message: string }
}

export type UmamiSessionData = {
  email?: string
  name?: string
  role?: string
  plan?: string
  mentor_id?: string
  is_whitelabel?: boolean
  whitelabel_domain?: string
  locale?: string
  app?: string
}

type UmamiApi = {
  track:
    | ((name: string, data?: UmamiEventPayload) => void)
    | ((fn: (props: Record<string, unknown>) => Record<string, unknown>) => void)
  identify: (uniqueId: string, sessionData?: UmamiSessionData) => void
}

declare global {
  interface Window {
    umami?: UmamiApi
  }
}

const MAX_EVENT_NAME_LENGTH = 50
const MAX_STRING_LENGTH = 500
const MAX_PROPERTIES = 50
const FORBIDDEN_NAME_START = /^[=+\-@]/

const hasUmami = () => typeof window !== 'undefined' && !!window.umami

// Set by useUmamiIdentify once the tenant (mentor) is known; merged into every event.
let currentMentorId: string | undefined

export function setAnalyticsMentorId(mentorId: string | undefined): void {
  currentMentorId = mentorId || undefined
}

/** Keeps only flat string / number / boolean values, truncating long strings. */
function sanitize(data: Record<string, unknown>): UmamiEventPayload {
  const clean: UmamiEventPayload = {}
  let count = 0
  for (const [key, value] of Object.entries(data)) {
    if (count >= MAX_PROPERTIES) break
    if (typeof value === 'string') {
      clean[key] = value.slice(0, MAX_STRING_LENGTH)
    } else if (typeof value === 'number' ? Number.isFinite(value) : typeof value === 'boolean') {
      clean[key] = value as number | boolean
    } else {
      continue
    }
    count += 1
  }
  return clean
}

type QueuedEvent = { name: string; payload: UmamiEventPayload | undefined }

const MAX_QUEUE = 50
const FLUSH_INTERVAL_MS = 200
const MAX_FLUSH_ATTEMPTS = 50

// Events fired before the deferred tracker script finished loading.
let pendingEvents: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let flushAttempts = 0

function stopFlushTimer(): void {
  if (flushTimer !== null) clearInterval(flushTimer)
  flushTimer = null
  flushAttempts = 0
}

function flushPendingEvents(): void {
  try {
    flushAttempts += 1
    if (hasUmami()) {
      const events = pendingEvents
      pendingEvents = []
      stopFlushTimer()
      for (const event of events) {
        try {
          ;(window.umami!.track as (n: string, d?: UmamiEventPayload) => void)(
            event.name,
            event.payload
          )
        } catch {
          // One bad event must not drop the rest.
        }
      }
      return
    }
    if (flushAttempts >= MAX_FLUSH_ATTEMPTS) {
      // Tracker never loaded (e.g. ad blocker): give up.
      pendingEvents = []
      stopFlushTimer()
    }
  } catch {
    pendingEvents = []
    stopFlushTimer()
  }
}

function enqueueEvent(event: QueuedEvent): void {
  pendingEvents.push(event)
  if (pendingEvents.length > MAX_QUEUE) pendingEvents.shift()
  if (flushTimer === null) {
    flushAttempts = 0
    flushTimer = setInterval(flushPendingEvents, FLUSH_INTERVAL_MS)
  }
}

/**
 * Sends a custom event to Umami.
 *
 * No-op during SSR and never throws. Events fired before the Jorney `script.js` loads
 * are queued and flushed in order once the tracker exists.
 */
export function trackEvent<K extends keyof UmamiEvents>(
  name: K,
  data?: UmamiEvents[K]
): void {
  try {
    if (typeof window === 'undefined') return
    const eventName = String(name)
    if (eventName.length > MAX_EVENT_NAME_LENGTH || FORBIDDEN_NAME_START.test(eventName)) return
    const payload = sanitize({
      ...(data as Record<string, unknown> | undefined),
      ...(currentMentorId ? { mentor_id: currentMentorId } : {}),
    })
    const finalPayload = Object.keys(payload).length ? payload : undefined
    if (!hasUmami()) {
      enqueueEvent({ name: eventName, payload: finalPayload })
      return
    }
    ;(window.umami!.track as (n: string, d?: UmamiEventPayload) => void)(
      eventName,
      finalPayload
    )
  } catch {
    // Analytics must never break the UI.
  }
}

/**
 * Identifies the logged-in user in Umami (distinct id + session data).
 */
export function identifyUser(
  uniqueId: string,
  sessionData?: UmamiSessionData
): void {
  try {
    if (!hasUmami() || !uniqueId) return
    const cleaned: UmamiSessionData | undefined = sessionData
      ? Object.fromEntries(
          Object.entries(sessionData).filter(([, v]) => v !== undefined)
        )
      : undefined
    window.umami!.identify(uniqueId, cleaned)
  } catch {
    // idem.
  }
}
